import { useQuery } from "@tanstack/react-query";
import { Badge } from "@tcg/ui";
import { useRealtime } from "@tcg/realtime/react";
import { Eye, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, usePos, useRegister } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import { POS_TOPICS } from "@/lib/sse";

type Transmission = "pending" | "exported" | "sent" | "accepted" | "rejected" | "cancelled";

function transmissionTone(status: string): {
  icon: string;
  variant: "pending" | "problem" | "outline";
} {
  if (status === "sent" || status === "accepted" || status === "exported") {
    return { icon: "✅", variant: "outline" };
  }
  if (status === "rejected") {
    return { icon: "⚠", variant: "problem" };
  }
  if (status === "cancelled") {
    return { icon: "—", variant: "outline" };
  }
  return { icon: "⏳", variant: "pending" };
}

/** Money of tax-service (`{amount: "21364", currencyCode: "JPY"}`) → the integer to show. */
function yenOf(money: { amount: string } | null | undefined): number {
  const value = Number.parseInt(money?.amount ?? "0", 10);
  return Number.isFinite(value) ? value : 0;
}

/**
 * 免税の記録 (T07-30): the 購入記録情報 of this shift at tax-service — the buyer masked
 * (`****1234`), how far the record got (⏳ / ✅ / ⚠ 事後送信) and 「再送信」. The full passport
 * number is shown only to someone with `kyc.view` (tax-service writes an audit entry).
 */
export function RecordsList() {
  const { t, i18n } = useTranslation();
  const { tax } = usePos();
  const register = useRegister();
  const toast = useToast();
  const canSend = useCan("tax.taxfree");
  const canView = useCan("kyc.view");
  const shiftId = register.data?.openShift?.shiftId ?? null;
  const [busy, setBusy] = useState<string | null>(null);
  const [shown, setShown] = useState<{ id: string; passport: string | null } | null>(null);
  const records = useQuery({
    queryKey: ["pos-tax-free-records", "list", shiftId ?? "none"],
    queryFn: () =>
      call(() =>
        tax.GET("/v1/pos/tax-free/records", {
          params: { query: { ...(shiftId ? { shiftId } : {}), limit: 50 } },
        }),
      ),
  });
  // a record reached tax-service later (the minute job) → ⏳ becomes ✅ by itself
  useRealtime([POS_TOPICS.sale], () => void records.refetch(), {
    onResync: () => void records.refetch(),
  });

  const resend = async (id: string) => {
    setBusy(id);
    try {
      await call(() =>
        tax.POST("/v1/pos/tax-free/records/{record_id}/retransmit", {
          params: { path: { record_id: id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        }),
      );
      toast.show(t("taxfree.resent"), "success");
      await records.refetch();
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(null);
    }
  };

  const reveal = async (id: string) => {
    setBusy(id);
    try {
      const record = await call(() =>
        tax.GET("/v1/admin/tax-free/records/{record_id}", { params: { path: { record_id: id } } }),
      );
      const pii = record.buyerPii as Record<string, string | null> | null | undefined;
      setShown({ id, passport: pii?.passportNumber ?? null });
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(null);
    }
  };

  const time = new Intl.DateTimeFormat(i18n.resolvedLanguage === "ja" ? "ja-JP" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  });
  const rows = records.data?.data ?? [];
  return (
    <section aria-labelledby="tf-records" className="flex min-h-0 flex-col gap-2">
      <h2 id="tf-records" className="text-xl font-semibold">
        {t("taxfree.records_title")}
      </h2>
      {records.isError ? (
        <p role="alert" className="text-pos-problem">
          {errorText(records.error, t)}
        </p>
      ) : null}
      {rows.length === 0 && !records.isLoading && !records.isError ? (
        <p className="text-muted-foreground">{t("taxfree.records_empty")}</p>
      ) : null}
      <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto">
        {rows.map((row) => {
          const tone = transmissionTone(row.transmissionStatus as Transmission);
          return (
            <li
              key={row.id}
              data-testid="tf-record"
              className="grid min-h-pos-touch grid-cols-[4rem_9rem_1fr_auto_auto_auto] items-center gap-3 rounded-md border px-3 py-2"
            >
              <span className="tabular-nums text-muted-foreground">
                {time.format(new Date(row.createdAt))}
              </span>
              <span className="font-mono">{row.orderNumber ?? ""}</span>
              <span className="flex flex-col">
                <span>{row.buyer.name}</span>
                <span className="font-mono text-sm" data-testid="tf-passport">
                  {shown?.id === row.id && shown.passport
                    ? shown.passport
                    : (row.buyer.passportNumberMasked ?? "")}
                </span>
              </span>
              <MoneyText amount={yenOf(row.documentTotalIncl)} />
              <Badge variant={tone.variant}>
                {tone.icon}{" "}
                {t(`taxfree.transmission_${row.transmissionStatus}`, {
                  defaultValue: row.transmissionStatus,
                })}
                {row.lateFlag ? ` · ${t("taxfree.late")}` : ""}
              </Badge>
              <span className="flex gap-1">
                {canSend &&
                (row.transmissionStatus === "pending" || row.transmissionStatus === "rejected") ? (
                  <button
                    type="button"
                    onClick={() => void resend(row.id)}
                    disabled={busy !== null}
                    className="flex min-h-12 items-center gap-1 rounded-md border px-3"
                  >
                    <RefreshCw aria-hidden className="size-4" />
                    {t("taxfree.resend")}
                  </button>
                ) : null}
                {canView ? (
                  <button
                    type="button"
                    onClick={() => void reveal(row.id)}
                    disabled={busy !== null}
                    aria-label={t("taxfree.show_passport")}
                    className="flex min-h-12 items-center gap-1 rounded-md border px-3"
                  >
                    <Eye aria-hidden className="size-4" />
                    {t("taxfree.show")}
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
      {shown ? (
        <Modal
          open
          title={t("taxfree.show_passport")}
          description={t("taxfree.show_audited")}
          onClose={() => setShown(null)}
        >
          <p className="font-mono text-2xl">{shown.passport ?? "—"}</p>
        </Modal>
      ) : null}
    </section>
  );
}
